import { useState } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { btnIcon, btnSecondary, inputCls } from '../ui/styles';
import { dayMedium, isIsoDate, today } from '../../core/format/dates';
import { OWNER_STAFF_ID, staffName } from '../../core/rules/salonDays';
import type { SalonDayException, StaffProfile } from '../../core/types/site';
type Props = {
  value: SalonDayException[];
  staff: StaffProfile[];
  onChange: (value: SalonDayException[]) => void;
};

/** Losse dagen die afwijken van de vaste week. Voorbije dagen vallen uit de lijst (ze doen niets meer). */
export function SalonExceptionsEditor({
  value,
  staff,
  onChange
}: Props) {
  const [date, setDate] = useState('');
  const now = today();
  const upcoming = value.filter(e => e.date >= now).sort((a, b) => a.date.localeCompare(b.date));
  const active = staff.filter(s => s.active);
  const taken = value.some(e => e.date === date);
  const set = (id: string, patch: Partial<SalonDayException>) => onChange(value.map(e => e.id === id ? {
    ...e,
    ...patch
  } : e));
  const remove = (id: string) => onChange(value.filter(e => e.id !== id));
  const add = () => {
    if (!isIsoDate(date) || date < now || taken) return;
    // Standaard dicht: dat is de meest voorkomende (vakantie). Open zetten is één vinkje.
    onChange([...value.filter(e => e.date >= now), {
      id: `uitz-${date}`,
      date
    }]);
    setDate('');
  };
  return <div className="space-y-4" data-id="element-4066" data-mp="12okae6vd9036">
      {upcoming.length === 0 ? <p className="text-[13px] text-muted" data-id="element-4067" data-mp="12mr26n53u7t2">Geen afwijkende dagen gepland.</p> : <ul className="divide-y divide-line" data-id="element-4068" data-mp="xnwmvv6cj1dp">
          {upcoming.map(e => {
        const open = Boolean(e.open && e.close);
        const wrong = open && (e.open ?? '') >= (e.close ?? '');
        return <li key={e.id} className="grid gap-2 py-3 sm:grid-cols-[7rem_auto_1fr_auto] sm:items-center" data-id="element-4069" data-mp="1lfla4x4b8zns">
                <span className="text-[14px] capitalize text-ink" data-id="element-4070" data-mp="1ggs1bhzyxwty">{dayMedium(e.date)}</span>
                <label className="flex items-center gap-2 text-[13px] text-muted" data-id="element-4071" data-mp="e3cdj4mnoypn">
                  <input type="checkbox" checked={open} onChange={ev => set(e.id, ev.target.checked ? {
              open: '09:00',
              close: '17:00'
            } : {
              open: undefined,
              close: undefined,
              staffId: undefined
            })} className="h-4 w-4 accent-clay" data-id="element-4072" data-mp="e19ejv3izv8s" />
                  Open<span className="sr-only" data-id="element-4073" data-mp="wud9djshce4z"> op {dayMedium(e.date)}</span>
                </label>
                {open ? <span className="flex flex-wrap items-center gap-2" data-id="element-4074" data-mp="11r3pu8875bqx">
                    <input type="time" value={e.open} onChange={ev => set(e.id, {
              open: ev.target.value
            })} className={`${inputCls} w-28`} aria-label="Open vanaf" aria-invalid={wrong} data-id="element-4075" data-mp="16rhynshmgpq0" />
                    <span className="text-muted" aria-hidden="true" data-id="element-4076" data-mp="38jmmosw032vg">
                      –
                    </span>
                    <input type="time" value={e.close} onChange={ev => set(e.id, {
              close: ev.target.value
            })} className={`${inputCls} w-28`} aria-label="Open tot" aria-invalid={wrong} data-id="element-4077" data-mp="3clrf5oapptgi" />
                    {active.length > 1 && <select value={e.staffId || OWNER_STAFF_ID} onChange={ev => set(e.id, {
              staffId: ev.target.value === OWNER_STAFF_ID ? undefined : ev.target.value
            })} className={`${inputCls} w-32`} aria-label="Wie werkt er" data-id="element-4078" data-mp="3095qibjf30bd">
                        {active.map(s => <option key={s.id} value={s.id} data-id="element-4079" data-mp="3j1rob1dvx0yl">
                            {s.name}
                          </option>)}
                        {e.staffId && !active.some(s => s.id === e.staffId) && <option value={e.staffId} data-id="element-4080" data-mp="3a2mm9l346pho">{staffName(staff, e.staffId)} (gestopt)</option>}
                      </select>}
                    {wrong && <span role="alert" className="basis-full text-[12px] text-clay" data-id="element-4081" data-mp="1yjtr7l99yakx">
                        Sluit vóór opening
                      </span>}
                  </span> : <span className="text-[13px] text-muted" data-id="element-4082" data-mp="3dh4rez69tfrb">Dicht</span>}
                <button type="button" onClick={() => remove(e.id)} className={btnIcon} aria-label={`${dayMedium(e.date)} weghalen`} data-id="element-4083" data-mp="3dfbifd86qjg5">
                  <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4084" data-mp="92elarb7x843" />
                </button>
              </li>;
      })}
        </ul>}
      <div className="flex flex-wrap items-center gap-2" data-id="element-4085" data-mp="28efoa0z6dw">
        <label className="sr-only" htmlFor="uitzondering-datum" data-id="element-4086" data-mp="1q9825dxhtu">
          Datum
        </label>
        <input id="uitzondering-datum" type="date" min={now} value={date} onChange={e => setDate(e.target.value)} className={`${inputCls} w-44`} data-id="element-4087" data-mp="5008anu8d2kq" />
        <button type="button" onClick={add} disabled={!date || date < now || taken} className={btnSecondary} data-id="element-4088" data-mp="2prk27yjixm9l">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4089" data-mp="2ha0bgj4nposd" />
          Dag toevoegen
        </button>
        {taken && <span className="text-[12px] text-clay" data-id="element-4090" data-mp="2d86cqge0uuuu">Deze dag staat er al.</span>}
      </div>
    </div>;
}